/**
 * Recall Check — hosted standalone landing page at /recall/<slug>.
 *
 * Explicitly public and unauthenticated: rendered from App.js BEFORE the
 * auth wall, and it never reads the auth context or a token.
 *
 * The card itself is the SAME shadow-DOM component the widget serves
 * (/api/recall-widget.js), mounted with variant="page" — so the page and
 * the widget can never drift.
 */
import { useEffect, useRef, useState } from "react";

const SCRIPT_SRC = `${window.location.origin}/api/recall-widget.js`;

function loadWidgetScript() {
  if (window.VDRecallCheck) return Promise.resolve(window.VDRecallCheck);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.VDRecallCheck));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(window.VDRecallCheck);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function RecallStandalonePage() {
  const slug = decodeURIComponent(window.location.pathname.replace(/^\/recall\/?/, "").split("/")[0] || "");
  const mountRef = useRef(null);
  const mountedRef = useRef(false);
  const [state, setState] = useState("loading"); // loading | ready | unavailable
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (!slug) { setState("unavailable"); return; }
    let cancelled = false;
    loadWidgetScript()
      .then((api) => {
        if (cancelled || mountedRef.current || !mountRef.current) return null;
        mountedRef.current = true;
        return api.mount(mountRef.current, { dealer: slug, variant: "page" });
      })
      .then((resolved) => {
        if (cancelled) return;
        if (resolved) { setCfg(resolved); setState("ready"); }
        else setState("unavailable");
      })
      .catch(() => !cancelled && setState("unavailable"));
    return () => { cancelled = true; };
  }, [slug]);

  if (state === "unavailable") {
    return (
      <div
        style={{ minHeight: "100vh", background: "#f4f6f7", display: "grid", placeItems: "center", padding: 24 }}
        data-testid="recall-page-unavailable"
      >
        <div style={{ maxWidth: 460, textAlign: "center", fontFamily: "'Hanken Grotesk', -apple-system, sans-serif" }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#12242f", margin: "0 0 8px" }}>
            Recall Check isn&apos;t available here
          </h1>
          <p style={{ fontSize: 14, color: "#5c6b76", margin: 0, lineHeight: 1.55 }}>
            This recall lookup page is no longer active. You can always check your VIN for free at{" "}
            <a href="https://www.nhtsa.gov/recalls" style={{ color: "#1c3f5e", fontWeight: 700 }}>
              nhtsa.gov/recalls
            </a>.
          </p>
        </div>
      </div>
    );
  }

  const brand = cfg?.brand_color || "#1c3f5e";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg,#f4f6f7 0%,#eaeef0 100%)",
        padding: "clamp(24px,6vw,72px) 20px",
        fontFamily: "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      data-testid="recall-standalone-page"
    >
      <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: "clamp(28px,5vw,56px)", gridTemplateColumns: "minmax(0,1fr)" }}>
        <header style={{ maxWidth: "56ch" }}>
          <div style={{ fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase", fontWeight: 800, color: brand, opacity: 0.85 }}>
            {cfg?.dealer_name || "Recall Check"}
          </div>
          <h1 style={{ fontSize: "clamp(30px,5vw,46px)", lineHeight: 1.05, fontWeight: 800, letterSpacing: "-0.03em", color: "#12242f", margin: "14px 0 12px" }}>
            Is there an open safety recall on your vehicle?
          </h1>
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "#5c6b76", margin: 0 }}>
            Enter your VIN below. We check the federal NHTSA database for open safety recalls on
            your exact vehicle. It&apos;s free, it takes seconds, and recall repairs are always
            performed at no charge.
          </p>
        </header>

        <div style={{ maxWidth: 520 }} ref={mountRef} data-testid="recall-page-card-mount" />

        <footer style={{ borderTop: "1px solid #dfe4e7", paddingTop: 18, fontSize: 12, color: "#7a8790", maxWidth: 680, lineHeight: 1.6 }}>
          Recall information is provided by the National Highway Traffic Safety Administration
          (NHTSA). A result of &quot;no open recalls&quot; reflects the federal database at the time of your
          search.{" "}
          {cfg?.service_phone && (
            <>
              Questions? Call {cfg?.dealer_name || "our service team"} at{" "}
              <a href={`tel:${String(cfg.service_phone).replace(/[^0-9+]/g, "")}`} style={{ color: brand, fontWeight: 700 }}>
                {cfg.service_phone}
              </a>.
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
